/**
 * EnableSaasModal — modal para habilitar SaaS em um tenant existente
 * (TASK-2437).
 *
 * Backend: POST /v1/admin/tenants/{tenantId}/enable-saas
 *
 * Opcionalmente o operador pode marcar "Adicionar primeiro sub-tenant agora"
 * → revela campos (name + envs) e o backend gera UM job atomico (OU SaaS +
 * sub + envs encadeados). Antes este fluxo fazia 2 POSTs (enable-saas
 * seguido de add-sub-tenant) e o segundo falhava com "Habilite SaaS
 * primeiro" porque o worker async ainda nao tinha criado a OU SaaS.
 */

import { useState } from 'react'
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Alert,
  Checkbox,
  TextInput,
  Box,
  Divider,
} from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import {
  awsOrgProvisioningApi,
  ENV_OPTIONS,
  PROJECT_NAME_REGEX,
  type EnvName,
} from '@/api/awsOrgProvisioning'
import { handleProvisioningActionError } from './handleProvisioningActionError'

interface EnableSaasModalProps {
  tenantId: number
  opened: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function EnableSaasModal({
  tenantId,
  opened,
  onClose,
  onSuccess,
}: EnableSaasModalProps) {
  const [addFirstSub, setAddFirstSub] = useState(false)
  const [subName, setSubName] = useState('')
  const [subEnvs, setSubEnvs] = useState<EnvName[]>([])

  const reset = () => {
    setAddFirstSub(false)
    setSubName('')
    setSubEnvs([])
  }

  const subNameValid = PROJECT_NAME_REGEX.test(subName)
  const subNameError =
    subName.length > 0 && !subNameValid
      ? 'Use apenas A-Z e 0-9, comece com letra.'
      : null
  const subFormValid = !addFirstSub || (subNameValid && subEnvs.length > 0)

  const mutation = useMutation({
    mutationFn: async () => {
      const firstSub =
        addFirstSub && subNameValid && subEnvs.length > 0
          ? { name: subName, environments: subEnvs }
          : undefined
      return awsOrgProvisioningApi.enableSaas(tenantId, firstSub)
    },
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        title: 'SaaS habilitado',
        message: addFirstSub
          ? `OU SaaS criada e sub-tenant ${subName} provisionado.`
          : 'OU SaaS criada com sucesso.',
      })
      reset()
      onSuccess?.()
      onClose()
    },
    onError: handleProvisioningActionError,
  })

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!mutation.isPending) {
          reset()
          onClose()
        }
      }}
      title="Habilitar SaaS"
      data-testid="enable-saas-modal"
      centered
    >
      <Stack gap="md">
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
        >
          Habilitar SaaS criara uma OU <b>SaaS</b> sob este tenant.
          E irreversível sem limpeza manual no AWS Organizations.
        </Alert>

        <Checkbox
          label="Adicionar primeiro sub-tenant agora"
          checked={addFirstSub}
          onChange={(e) => setAddFirstSub(e.currentTarget.checked)}
          data-testid="enable-saas-add-first"
        />

        {addFirstSub && (
          <>
            <Divider />
            <TextInput
              label="Nome do sub-tenant"
              placeholder="GETNET"
              value={subName}
              onChange={(e) => setSubName(e.currentTarget.value.toUpperCase())}
              required
              error={subNameError}
              data-testid="enable-saas-sub-name"
            />
            <Box>
              <Text size="sm" fw={500} mb={4}>
                Ambientes
              </Text>
              <Checkbox.Group
                value={subEnvs}
                onChange={(values) => setSubEnvs(values as EnvName[])}
              >
                <Group gap="lg">
                  {ENV_OPTIONS.map((e) => (
                    <Checkbox
                      key={e}
                      value={e}
                      label={e}
                      data-testid={`enable-saas-sub-env-${e}`}
                    />
                  ))}
                </Group>
              </Checkbox.Group>
            </Box>
          </>
        )}

        <Group justify="flex-end" mt="sm">
          <Button
            variant="default"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            color="yellow"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!subFormValid}
            data-testid="enable-saas-confirm"
          >
            Habilitar SaaS
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
